import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Users, UserCheck, UserX, Check, Plus,
    Calendar, Clock
} from 'lucide-react';
import { CardStats, Button, ConfirmModal, ExportDropdown } from '@components/ui';
import { useExportExcel } from '@hooks';
import {
    getPersonnelByRole,
    getAttendanceForDate,
    getPersonnelStats,
    getRoleLabel
} from '@mocks/personel';
import PersonelModal from './PersonelModal';
import PersonelYoklama from './PersonelYoklama';
import PersonelPuantaj from './PersonelPuantaj';
import clsx from 'clsx';

export default function Personel() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [activeTab, setActiveTab] = useState('yoklama');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);

    // Yoklama state
    const [attendance, setAttendance] = useState(() => getAttendanceForDate(selectedDate));
    const [hasChanges, setHasChanges] = useState(false);

    const allPersonnel = useMemo(() => getPersonnelByRole('all', constructionId), [constructionId]);
    const stats = getPersonnelStats(allPersonnel, attendance);

    // Export
    const { exportToExcel, isExporting } = useExportExcel();

    const handleExportExcel = () => {
        exportToExcel({
            data: allPersonnel.map(p => ({
                'Ad Soyad': p.name,
                'Rol': getRoleLabel(p.role),
                'Telefon': p.phone || '-',
                'Günlük Ücret': p.dailyWage,
                'Yoklama': attendance[p.id] ? 'Geldi' : 'Gelmedi',
            })),
            columns: [
                { header: 'Ad Soyad', key: 'Ad Soyad' },
                { header: 'Rol', key: 'Rol' },
                { header: 'Telefon', key: 'Telefon' },
                { header: 'Günlük Ücret', key: 'Günlük Ücret', format: 'currency' },
                { header: 'Yoklama', key: 'Yoklama' },
            ],
            fileName: `personel_yoklama_${selectedDate}`,
            sheetName: 'Personel',
        });
    };

    const handleDateChange = (date) => {
        if (hasChanges) {
            if (!window.confirm('Kaydedilmemiş değişiklikler var. Devam etmek istiyor musunuz?')) return;
        }
        setSelectedDate(date);
        setAttendance(getAttendanceForDate(date));
        setHasChanges(false);
    };

    const saveAttendance = () => {
        console.log('Yoklama kaydedildi:', attendance);
        toast.success(`${selectedDate} tarihli yoklama kaydedildi`);
        setHasChanges(false);
    };

    const handleAddPerson = () => { setSelectedPerson(null); setModalOpen(true); };

    const confirmDelete = () => {
        toast.success(`${selectedPerson?.name} silindi`);
        setDeleteModalOpen(false);
        setSelectedPerson(null);
    };

    const handleModalSubmit = (data) => {
        toast.success(selectedPerson ? 'Personel güncellendi' : 'Personel eklendi');
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            weekday: 'long', day: 'numeric', month: 'long',
        });
    };

    const presentCount = allPersonnel.filter(p => attendance[p.id]).length;
    const absentCount = allPersonnel.length - presentCount;

    const tabs = [
        { key: 'yoklama', label: 'Yoklama', icon: UserCheck },
        { key: 'puantaj', label: 'Puantaj', icon: Clock },
    ];

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Personel & Yoklama</h1>
                    <p className="text-text-tertiary mt-1">Günlük yoklama, puantaj ve personel yönetimi</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <ExportDropdown onExportExcel={handleExportExcel} isExporting={isExporting} />
                    <Button variant="primary" icon={<Plus size={18} />} onClick={handleAddPerson}>
                        Personel Ekle
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CardStats title="Toplam Personel" value={stats.activeCount} icon={Users} color="primary" size="sm" />
                <CardStats title="Bugün Gelen" value={presentCount} icon={UserCheck} color="success" size="sm" />
                <CardStats title="Bugün Gelmeyen" value={absentCount} icon={UserX} color="error" size="sm" />
                <CardStats
                    title="Devam Oranı"
                    value={`%${allPersonnel.length > 0 ? Math.round((presentCount / allPersonnel.length) * 100) : 0}`}
                    icon={Calendar}
                    color="info"
                    size="sm"
                />
            </div>

            {/* Tarih + Sekme Switcher */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Tarih Seçici (yalnızca yoklama sekmesinde) */}
                {activeTab === 'yoklama' && (
                    <div className="flex items-center gap-3">
                        <Calendar size={20} className="text-text-muted" />
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => handleDateChange(e.target.value)}
                            className="bg-bg-tertiary border border-border-light rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
                        />
                        <span className="text-text-secondary hidden sm:inline">
                            {formatDate(selectedDate)}
                        </span>
                    </div>
                )}

                {/* Kaydet Butonu */}
                {activeTab === 'yoklama' && hasChanges && (
                    <Button variant="success" icon={<Check size={18} />} onClick={saveAttendance}>
                        Yoklamayı Kaydet
                    </Button>
                )}

                {/* Sekme Switcher */}
                <div className="flex items-center gap-1 bg-bg-tertiary rounded-lg p-1 md:ml-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={clsx(
                                "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all",
                                activeTab === tab.key
                                    ? "bg-primary text-white shadow-sm"
                                    : "text-text-secondary hover:text-text-primary hover:bg-bg-hover"
                            )}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'yoklama' ? (
                <PersonelYoklama
                    attendance={attendance}
                    setAttendance={setAttendance}
                    hasChanges={hasChanges}
                    setHasChanges={setHasChanges}
                    onSaveAttendance={saveAttendance}
                    allPersonnel={allPersonnel}
                />
            ) : (
                <PersonelPuantaj />
            )}

            {/* Floating Save Bar */}
            {hasChanges && activeTab === 'yoklama' && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-bg-secondary border border-border-light rounded-xl shadow-lg p-4 flex items-center gap-4 z-50">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-success">
                            <UserCheck size={18} />
                            <span className="font-medium">{presentCount} Geldi</span>
                        </div>
                        <div className="w-px h-4 bg-border-light" />
                        <div className="flex items-center gap-2 text-error">
                            <UserX size={18} />
                            <span className="font-medium">{absentCount} Gelmedi</span>
                        </div>
                    </div>
                    <Button variant="success" icon={<Check size={18} />} onClick={saveAttendance}>
                        Kaydet
                    </Button>
                </div>
            )}

            {/* Modals */}
            <PersonelModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleModalSubmit}
                personnel={selectedPerson}
            />

            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Personeli Sil"
                message={`"${selectedPerson?.name}" personelini silmek istediğinize emin misiniz?`}
                confirmText="Evet, Sil"
                variant="danger"
            />
        </div>
    );
}
